/**
 * Ícones do sistema — Phosphor, num só lugar.
 *
 * Toda tela importa daqui, nunca da biblioteca: o estilo visual (peso,
 * duotone) é decidido uma vez, e trocar de família de ícones vira mudar
 * este arquivo.
 *
 * Pesos:
 *   - regular (padrão): só o contorno — navegação, atalhos, estados e ações;
 *   - bold: glifos de interface pequenos (fechar, check, setas, setinha de
 *     menu, busca), que no duotone ficariam borrados;
 *   - fill: passe `weight="fill"` para estados ativos (item de menu
 *     selecionado, aula curtida, botão de play).
 */
import {
  Archive as PArchive,
  ArrowCounterClockwise as PArrowCounterClockwise,
  ArrowLeft as PArrowLeft,
  ArrowRight as PArrowRight,
  ArrowSquareOut as PArrowSquareOut,
  Article as PArticle,
  Bank as PBank,
  Basket as PBasket,
  BookOpen as PBookOpen,
  Broadcast as PBroadcast,
  CalendarDots as PCalendarDots,
  CaretDown as PCaretDown,
  Check as PCheck,
  CheckCircle as PCheckCircle,
  CircleNotch as PCircleNotch,
  Clock as PClock,
  ClockCounterClockwise as PClockCounterClockwise,
  Copy as PCopy,
  Desktop as PDesktop,
  DeviceMobile as PDeviceMobile,
  DeviceTablet as PDeviceTablet,
  EnvelopeSimple as PEnvelopeSimple,
  Eye as PEye,
  EyeSlash as PEyeSlash,
  FilmStrip as PFilmStrip,
  Flask as PFlask,
  GraduationCap as PGraduationCap,
  Handshake as PHandshake,
  Heart as PHeart,
  Hourglass as PHourglass,
  House as PHouse,
  LinkSimple as PLinkSimple,
  List as PList,
  LockSimple as PLockSimple,
  MagnifyingGlass as PMagnifyingGlass,
  MapPin as PMapPin,
  MonitorPlay as PMonitorPlay,
  Moon as PMoon,
  NotePencil as PNotePencil,
  Package as PPackage,
  PaperPlaneRight as PPaperPlaneRight,
  Play as PPlay,
  PlayCircle as PPlayCircle,
  Plus as PPlus,
  Question as PQuestion,
  RocketLaunch as PRocketLaunch,
  ShareNetwork as PShareNetwork,
  ShieldCheck as PShieldCheck,
  ShieldWarning as PShieldWarning,
  SidebarSimple as PSidebarSimple,
  SignIn as PSignIn,
  SignOut as PSignOut,
  Sparkle as PSparkle,
  SquaresFour as PSquaresFour,
  Stack as PStack,
  Storefront as PStorefront,
  Sun as PSun,
  Television as PTelevision,
  Tray as PTray,
  Trash as PTrash,
  TrendUp as PTrendUp,
  UploadSimple as PUploadSimple,
  User as PUser,
  Users as PUsers,
  Warning as PWarning,
  WarningCircle as PWarningCircle,
  X as PX,
  PencilSimple as PPencilSimple,
  Buildings as PBuildings,
  Funnel as PFunnel,
  UserPlus as PUserPlus,
  Images as PImages,
  Gauge as PGauge,
  Printer as PPrinter,
  Star as PStar,
  DownloadSimple as PDownloadSimple,
  ChartLineUp as PChartLineUp,
  ChartBar as PChartBar,
  CaretRight as PCaretRight,
  ArrowUp as PArrowUp,
  ArrowDown as PArrowDown,
  Paperclip as PPaperclip,
  FileDoc as PFileDoc,
  FileImage as PFileImage,
  FilePdf as PFilePdf,
  FilePpt as PFilePpt,
  FileText as PFileText,
  FileXls as PFileXls,
  Files as PFiles,
  ArrowElbowDownRight as PArrowElbowDownRight,
  ArrowsSplit as PArrowsSplit,
  BracketsCurly as PBracketsCurly,
  CalendarBlank as PCalendarBlank,
  ChatCircle as PChatCircle,
  ChatText as PChatText,
  CursorClick as PCursorClick,
  DotsThreeVertical as PDotsThreeVertical,
  Flag as PFlag,
  FlowArrow as PFlowArrow,
  Hash as PHash,
  ImageSquare as PImageSquare,
  Info as PInfo,
  Lightbulb as PLightbulb,
  ListChecks as PListChecks,
  Phone as PPhone,
  StopCircle as PStopCircle,
  Textbox as PTextbox,
  VideoCamera as PVideoCamera,
  WebhooksLogo as PWebhooksLogo,
  Cards as PCards,
  type Icon as PhosphorIcon,
  type IconProps,
  type IconWeight,
} from '@phosphor-icons/react';
import { forwardRef } from 'react';

/** Tipo de um componente de ícone — para props como `icon: Icon`. */
export type Icon = PhosphorIcon;
export type { IconProps, IconWeight };

function weighted(Base: PhosphorIcon, weight: IconWeight): Icon {
  const Wrapped = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <Base ref={ref} weight={weight} {...props} />
  ));
  Wrapped.displayName = Base.displayName;
  return Wrapped as Icon;
}

// Backoffice: traço simples, sem preenchimento — menos cor, como os
// sistemas internos do grupo. O nome `duo` fica para não mexer na lista.
const duo = (Base: PhosphorIcon) => weighted(Base, 'regular');
const bold = (Base: PhosphorIcon) => weighted(Base, 'bold');

// ───────────── navegação ─────────────
export const House = duo(PHouse);
export const Handshake = duo(PHandshake);
export const CalendarDots = duo(PCalendarDots);
export const MonitorPlay = duo(PMonitorPlay);
export const Storefront = duo(PStorefront);
export const Package = duo(PPackage);
export const SquaresFour = duo(PSquaresFour);
export const SidebarSimple = duo(PSidebarSimple);
export const List = bold(PList);

// ───────────── conteúdo ─────────────
export const BookOpen = duo(PBookOpen);
export const Article = duo(PArticle);
export const GraduationCap = duo(PGraduationCap);
export const Play = duo(PPlay);
export const PlayCircle = duo(PPlayCircle);
export const FilmStrip = duo(PFilmStrip);
export const Broadcast = duo(PBroadcast);
export const Question = duo(PQuestion);
export const Stack = duo(PStack);
export const Clock = duo(PClock);
export const ClockCounterClockwise = duo(PClockCounterClockwise);
export const Hourglass = duo(PHourglass);
export const NotePencil = duo(PNotePencil);
export const Flask = duo(PFlask);
export const Basket = duo(PBasket);
export const Bank = duo(PBank);
export const MapPin = duo(PMapPin);
export const Sparkle = duo(PSparkle);
export const TrendUp = duo(PTrendUp);
export const Tray = duo(PTray);

// ───────────── ações ─────────────
export const Heart = duo(PHeart);
export const ShareNetwork = duo(PShareNetwork);
export const PaperPlaneRight = duo(PPaperPlaneRight);
export const UploadSimple = duo(PUploadSimple);
export const Copy = duo(PCopy);
export const LinkSimple = duo(PLinkSimple);
export const Trash = duo(PTrash);
export const Archive = duo(PArchive);
export const RocketLaunch = duo(PRocketLaunch);
export const ArrowCounterClockwise = duo(PArrowCounterClockwise);
export const ArrowSquareOut = bold(PArrowSquareOut);
export const EnvelopeSimple = duo(PEnvelopeSimple);
export const SignIn = duo(PSignIn);
export const SignOut = duo(PSignOut);
export const Eye = duo(PEye);
export const EyeSlash = duo(PEyeSlash);
export const Sun = duo(PSun);
export const Moon = duo(PMoon);

// ───────────── estado ─────────────
export const CheckCircle = duo(PCheckCircle);
export const Warning = duo(PWarning);
export const WarningCircle = duo(PWarningCircle);
export const ShieldCheck = duo(PShieldCheck);
export const ShieldWarning = duo(PShieldWarning);
export const LockSimple = duo(PLockSimple);
export const User = duo(PUser);
export const Users = duo(PUsers);
export const CircleNotch = bold(PCircleNotch);

// ───────────── dispositivos ─────────────
export const Desktop = duo(PDesktop);
export const DeviceMobile = duo(PDeviceMobile);
export const DeviceTablet = duo(PDeviceTablet);
export const Television = duo(PTelevision);

// ───────────── glifos de interface ─────────────
export const X = bold(PX);
export const Check = bold(PCheck);
export const Plus = bold(PPlus);
export const ArrowLeft = bold(PArrowLeft);
export const ArrowRight = bold(PArrowRight);
export const CaretDown = bold(PCaretDown);
export const MagnifyingGlass = bold(PMagnifyingGlass);
export const ArrowUp = bold(PArrowUp);
export const ArrowDown = bold(PArrowDown);
export const CaretRight = bold(PCaretRight);

// ───────────── backoffice ─────────────
export const ChartBar = duo(PChartBar);
export const ChartLineUp = duo(PChartLineUp);
export const DownloadSimple = duo(PDownloadSimple);
export const Gauge = duo(PGauge);
export const Images = duo(PImages);
export const Star = duo(PStar);
export const Printer = duo(PPrinter);
export const UserPlus = duo(PUserPlus);
export const Funnel = duo(PFunnel);
export const Buildings = duo(PBuildings);
export const PencilSimple = duo(PPencilSimple);

// ───────────── arquivos (materiais da aula, envio em lote) ─────────────
export const Paperclip = duo(PPaperclip);
export const FilePdf = duo(PFilePdf);
export const FileImage = duo(PFileImage);
export const FileDoc = duo(PFileDoc);
export const FilePpt = duo(PFilePpt);
export const FileXls = duo(PFileXls);
export const FileText = duo(PFileText);
export const Files = duo(PFiles);

// ───────────── fluxos da Centuria (blocos do builder, atalhos) ─────────────
export const FlowArrow = duo(PFlowArrow);
export const ChatText = duo(PChatText);
export const ChatCircle = duo(PChatCircle);
export const ImageSquare = duo(PImageSquare);
export const VideoCamera = duo(PVideoCamera);
export const ListChecks = duo(PListChecks);
export const Textbox = duo(PTextbox);
export const Phone = duo(PPhone);
export const Hash = duo(PHash);
export const CalendarBlank = duo(PCalendarBlank);
export const ArrowsSplit = duo(PArrowsSplit);
export const BracketsCurly = duo(PBracketsCurly);
export const ArrowElbowDownRight = duo(PArrowElbowDownRight);
export const StopCircle = duo(PStopCircle);
export const WebhooksLogo = duo(PWebhooksLogo);
export const Lightbulb = duo(PLightbulb);
export const Flag = duo(PFlag);
export const CursorClick = duo(PCursorClick);
export const Info = duo(PInfo);
export const DotsThreeVertical = bold(PDotsThreeVertical);
export const Cards = duo(PCards);
